import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Download, Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, SectionTitle, Field, LoadingState, ErrorState, StatusBadge, ConfirmDialog } from "@/components/common";
import { QuoteDocument, TotalsBlock } from "@/modules/quotes/QuoteDocument";
import { useGarage } from "@/context/GarageContext";
import { PlanGate } from "@/components/PlanGate";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, downloadFile } from "@/lib/api";
import { fmtCents } from "@/lib/format";
import { t } from "@/i18n";
import { PartPicker } from "@/modules/inventory/PartPicker";
import { Breadcrumbs, StatusBar } from "@/components/layout/Record";
import { TemplatePicker } from "@/modules/quotes/JobTemplates";
import { SecureLinkButton } from "@/modules/secure_links/SecureLinkButton";

export const toForm = (q) => ({
  lines: q.lines.map((l) => ({ id: l.id, type: l.type, description: l.description, qty: String(l.qty), unit_price: (Math.abs(l.unit_price_cents) / 100).toFixed(2),
    discount_pct: String(l.discount_pct || 0), cost_price: l.cost_price_cents != null ? (l.cost_price_cents / 100).toFixed(2) : "", part_id: l.part_id || null })),
  discount_type: q.discount_type, discount_value: q.discount_type === "amount" ? (q.discount_value / 100).toFixed(2) : String(q.discount_value || 0),
  valid_until: q.valid_until || "", terms: q.terms || "", notes: q.notes || "",
});
export const toBody = (f) => ({
  ...f, discount_value: f.discount_value || 0,
  lines: f.lines.map((l) => ({ ...l, qty: l.qty || 0, unit_price: l.unit_price || 0, discount_pct: l.discount_pct || 0, cost_price: l.type === "part" && l.cost_price !== "" ? l.cost_price : null })),
});

export function LineRow({ l, i, set, remove, types = ["part", "labour", "other"] }) {
  return (
    <div className="grid grid-cols-12 gap-2 rounded-lg border border-slate-200 p-2" data-testid={`quote-line-${i}`}>
      <Select value={l.type} onValueChange={(v) => set(i, { type: v })}>
        <SelectTrigger className="col-span-4 sm:col-span-2" data-testid={`line-${i}-type`}><SelectValue /></SelectTrigger>
        <SelectContent>{[...new Set([...types, l.type])].map((x) => <SelectItem key={x} value={x}>{t(`status.${x}`)}</SelectItem>)}</SelectContent>
      </Select>
      <Input className="col-span-8 sm:col-span-3" value={l.description} onChange={(e) => set(i, { description: e.target.value })} placeholder={t("quotes.description")} data-testid={`line-${i}-description`} />
      <Input className="col-span-3 sm:col-span-1" type="number" step="0.01" min="0" value={l.qty} onChange={(e) => set(i, { qty: e.target.value })} aria-label={t("quotes.qty")} data-testid={`line-${i}-qty`} />
      <Input className="col-span-3 sm:col-span-2" type="number" step="0.01" min="0" value={l.unit_price} onChange={(e) => set(i, { unit_price: e.target.value })} aria-label={t("quotes.unit")} placeholder={t("quotes.unitExGst")} data-testid={`line-${i}-price`} />
      <Input className="col-span-2 sm:col-span-1" type="number" step="0.01" min="0" max="100" value={l.discount_pct} onChange={(e) => set(i, { discount_pct: e.target.value })} aria-label={t("quotes.disc")} data-testid={`line-${i}-discount`} />
      <Input className="col-span-3 sm:col-span-2" type="number" step="0.01" min="0" disabled={l.type !== "part"} value={l.cost_price} onChange={(e) => set(i, { cost_price: e.target.value })} placeholder={t("quotes.cost")} title={t("quotes.costHint")} data-testid={`line-${i}-cost`} />
      <Button variant="ghost" size="icon" className="col-span-1 text-red-600" onClick={() => remove(i)} aria-label={t("common.remove")} data-testid={`line-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>
      {l.type === "part" && <PartPicker i={i} partId={l.part_id} onPick={(p) => set(i, p ? { part_id: p.id, description: `${p.name} (${p.sku})`, unit_price: (p.sell_cents / 100).toFixed(2), cost_price: (p.cost_cents / 100).toFixed(2) } : { part_id: null })} />}
    </div>
  );
}

function Editor({ quote, onSaved }) {
  const { garage } = useGarage();
  const [f, setF] = useState(toForm(quote));
  const [preview, setPreview] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const up = (patch) => { setF((x) => ({ ...x, ...patch })); setDirty(true); };
  const setLine = (i, patch) => up({ lines: f.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  useEffect(() => {
    const id = setTimeout(() => api.post("/quotes/calc", toBody(f)).then((r) => setPreview(r.data)).catch(() => {}), 300);
    return () => clearTimeout(id);
  }, [f]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/quotes/${quote.id}`, toBody(f));
      toast.success(data.id !== quote.id ? t("quotes.newVersionCreated", { v: data.version }) : t("common.saved"));
      setDirty(false);
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const draft = quote.status === "draft";
  return (
    <Card testId="quote-editor">
      {!draft && <p className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="quote-version-warning">{t("quotes.versionWarning")}</p>}
      <SectionTitle hint={t("quotes.linesHint")}>{t("quotes.lines")}</SectionTitle>
      <div className="space-y-2">{f.lines.map((l, i) => <LineRow key={l.id || i} l={l} i={i} set={setLine} remove={(j) => up({ lines: f.lines.filter((_, k) => k !== j) })} />)}</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {["part", "labour", "other"].map((ty) => <Button key={ty} variant="outline" size="sm" onClick={() => up({ lines: [...f.lines, { type: ty, description: "", qty: "1", unit_price: "", discount_pct: "0", cost_price: "" }] })} data-testid={`add-line-${ty}`}><Plus className="mr-1 h-3.5 w-3.5" /> {t(`status.${ty}`)}</Button>)}
        <TemplatePicker onAdd={(lines) => up({ lines: [...f.lines, ...lines] })} />
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <Field label={t("quotes.quoteDiscount")}>
          <div className="flex gap-2">
            <Select value={f.discount_type} onValueChange={(v) => up({ discount_type: v })}>
              <SelectTrigger className="w-28" data-testid="quote-discount-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["none", "pct", "amount"].map((x) => <SelectItem key={x} value={x}>{t(`quotes.discountTypes.${x}`)}</SelectItem>)}</SelectContent>
            </Select>
            {f.discount_type !== "none" && <Input type="number" step="0.01" min="0" value={f.discount_value} onChange={(e) => up({ discount_value: e.target.value })} data-testid="quote-discount-value" />}
          </div>
        </Field>
        <Field label={t("quotes.validUntil")}><Input type="date" value={f.valid_until} onChange={(e) => up({ valid_until: e.target.value })} data-testid="quote-valid-until" /></Field>
        <div>{f.lines.length === 0 ? <p className="text-sm text-slate-500" data-testid="quote-preview-empty">{t("quotes.addLineForTotals")}</p> : preview && <TotalsBlock totals={preview} currency={garage.currency} testId="quote-preview-totals" />}</div>
        <Field label={t("quotes.notes")} className="sm:col-span-3"><Textarea value={f.notes} onChange={(e) => up({ notes: e.target.value })} data-testid="quote-notes" /></Field>
        <Field label={t("quotes.terms")} hint={t("quotes.termsHint")} className="sm:col-span-3"><Textarea rows={4} value={f.terms} onChange={(e) => up({ terms: e.target.value })} data-testid="quote-terms" /></Field>
      </div>
      <Button className="mt-4" disabled={busy || !dirty} onClick={save} data-testid="quote-save">{draft ? t("quotes.saveDraft") : t("quotes.saveNewVersion")}</Button>
      {dirty && <span className="ml-3 text-xs text-amber-700">{t("quotes.unsaved")}</span>}
    </Card>
  );
}

export default function QuoteEditor() {
  return <PlanGate feature="quotes_and_invoices"><QuoteEditorInner /></PlanGate>;
}

function QuoteEditorInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { garage, readOnly, isManager } = useGarage();
  const { data, error, loading, reload } = useFetch(`/quotes/${id}`);
  const [confirmSend, setConfirmSend] = useState(false);
  const [revise, setRevise] = useState(false);
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const q = data;
  const send = async () => {
    setConfirmSend(false);
    try {
      await api.post(`/quotes/${q.id}/send`);
      toast.success(t("quotes.sentToast"));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const onSaved = (nq) => (nq.id !== q.id ? navigate(`/pro/quotes/${nq.id}`) : reload());
  return (
    <div className="space-y-6" data-testid="quote-page">
      <Breadcrumbs testId="quote-crumbs" items={[[t("b13.nav.sales"), "/pro/quotes"], [t("quotes.title"), "/pro/quotes"], ...(q.job_id ? [[t("quotes.backToJob"), `/pro/jobs/${q.job_id}`]] : []), [`${q.number} · v${q.version}`]]} />
      <StatusBar steps={["draft", "sent", ["declined", "expired", "superseded"].includes(q.status) ? q.status : "accepted"]} current={q.status} />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-sm font-semibold text-teal-700" data-testid="quote-number">{q.number} · v{q.version}</p>
          <h1 className="font-display text-3xl font-extrabold">{q.customer.name}</h1>
          <p className="text-sm text-slate-600">{q.vehicle ? `${q.vehicle.plate} · ${q.vehicle.make} ${q.vehicle.model}` : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={q.status} testId="quote-status" />
          <Button variant="outline" onClick={() => downloadFile(`/quotes/${q.id}/pdf`).catch((e) => toast.error(errMsg(e)))} data-testid="quote-pdf"><Download className="mr-2 h-4 w-4" /> PDF</Button>
          {q.status === "draft" && !readOnly && <Button onClick={() => setConfirmSend(true)} data-testid="quote-send"><Send className="mr-2 h-4 w-4" /> {t("quotes.send")}</Button>}
          {q.status === "sent" && q.is_current && !readOnly && <SecureLinkButton kind="quote" refId={q.id} />}
          {q.status === "accepted" && isManager && !readOnly && <Button onClick={() => api.post("/invoices", { quote_ids: [q.id] }).then((r) => navigate(`/pro/invoices/${r.data.id}`)).catch((e) => toast.error(errMsg(e)))} data-testid="quote-create-invoice">{t("invoices.fromQuote")}</Button>}
        </div>
      </div>
      <div className="grid gap-6 xl:grid-cols-[1fr_260px]">
        <div className="space-y-6">
          {q.editable && !readOnly && q.status !== "draft" && !revise && <Button variant="outline" onClick={() => setRevise(true)} data-testid="quote-revise">{t("quotes.revise")}</Button>}
          {q.editable && !readOnly && (q.status === "draft" || revise) && <Editor key={q.id + q.version} quote={q} onSaved={onSaved} />}
          {q.status !== "draft" && <QuoteDocument quote={q} garage={garage} />}
        </div>
        <Card testId="quote-versions">
          <SectionTitle>{t("quotes.versions")}</SectionTitle>
          <ul className="space-y-2 text-sm">
            {q.versions.map((v) => (
              <li key={v.id}><Link to={`/pro/quotes/${v.id}`} className={`flex items-center justify-between rounded-lg border p-2 ${v.id === q.id ? "border-[#0A2540]" : "border-slate-200"}`} data-testid={`quote-version-${v.version}`}>
                <span>v{v.version} · {fmtCents(v.total_cents)}</span><StatusBadge status={v.status} /></Link></li>
            ))}
          </ul>
          {q.status === "accepted" && <p className="mt-4 text-xs text-slate-500">{t("quotes.acceptedLocked")}</p>}
        </Card>
      </div>
      <ConfirmDialog open={confirmSend} onOpenChange={setConfirmSend} title={t("quotes.sendTitle")} description={t("quotes.sendText")} confirmLabel={t("quotes.send")} onConfirm={send} testId="quote-send-dialog" />
    </div>
  );
}
